/* eslint-disable prettier/prettier */
import { Folder, FileText, User, Building2, ShieldCheck, Mail } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardService } from '../../services/dashboard.service';

type EntityResult<T> = { items: T[]; total: number } | undefined;

type SectionProps<T> = {
  title: string;
  icon?: React.ReactNode;
  data: EntityResult<T>;
  renderItem: (item: T) => React.ReactNode;
  onShowMore?: () => void;
  onShowLess?: () => void;
  canShowLess?: boolean;
  loadingSection?: boolean; 
};

const Section = <T,>({
  title,
  icon,
  data,
  renderItem,
  onShowMore,
  onShowLess,
  canShowLess,
  loadingSection = false,
}: SectionProps<T>) => {
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const canShowMore = items.length < total;

  if (items.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="flex items-center gap-2 mb-3 text-xl font-semibold text-primary">
        {icon}
        {title}
        <span className="text-sm font-normal text-muted-foreground">({total || items.length})</span>
      </h2>

      <div className="space-y-2">
        {items.map(renderItem)}
      </div>

      <div className="flex gap-4 mt-3">
        {canShowMore && onShowMore && (
          <button
            onClick={onShowMore}
            disabled={loadingSection} 
            className={`text-sm font-medium underline text-primary hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            Show more
          </button>
        )}
        {canShowLess && onShowLess && !loadingSection && (
          <button
            onClick={onShowLess}
            className="text-sm font-medium underline text-primary hover:opacity-80"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

const DEFAULT_TAKE = 5;
const STEP = 10;

export const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [results, setResults] = useState<any | null>(null);

  const [takeUsers, setTakeUsers] = useState(DEFAULT_TAKE);
  const [takeRoles, setTakeRoles] = useState(DEFAULT_TAKE);
  const [takeFiles, setTakeFiles] = useState(DEFAULT_TAKE);
  const [takeFolders, setTakeFolders] = useState(DEFAULT_TAKE);
  const [takeInvitations, setTakeInvitations] = useState(DEFAULT_TAKE);
  const [takeCompanies, setTakeCompanies] = useState(DEFAULT_TAKE);

  const [pendingUsers, setPendingUsers] = useState(false);
  const [pendingRoles, setPendingRoles] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(false);
  const [pendingFolders, setPendingFolders] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState(false);
  const [pendingCompanies, setPendingCompanies] = useState(false);

  useEffect(() => {
    setTakeUsers(DEFAULT_TAKE);
    setPendingUsers(false);
    setTakeRoles(DEFAULT_TAKE);
    setPendingRoles(false);
    setTakeFiles(DEFAULT_TAKE);
    setPendingFiles(false);
    setTakeFolders(DEFAULT_TAKE);
    setPendingFolders(false);
    setTakeInvitations(DEFAULT_TAKE);
    setPendingInvitations(false);
    setTakeCompanies(DEFAULT_TAKE);
    setPendingCompanies(false);
  }, [query]);

  useEffect(() => {
    let isMounted = true;
    const fetchResults = async () => {
      if (!query) {
        setResults(null);
        return;
      }
      try {
        const data = await DashboardService.searchGlobally(query, {
          takeUsers,
          takeRoles,
          takeFiles,
          takeFolders,
          takeInvitations,
          takeCompanies,
        });
        if (!isMounted) return;
        setResults(data);
      } catch (err) {
        console.error('Search failed:', err);
        if (!isMounted) return;
        setResults(null);
      } finally {
        if (isMounted) {
          setPendingUsers(false);
          setPendingRoles(false);
          setPendingFiles(false);
          setPendingFolders(false);
          setPendingInvitations(false);
          setPendingCompanies(false);
        }
      }
    };

    fetchResults();
    return () => {
      isMounted = false;
    };
  }, [query, takeUsers, takeRoles, takeFiles, takeFolders, takeInvitations, takeCompanies]);

  const users = results?.users as EntityResult<any>;
  const roles = results?.roles as EntityResult<any>;
  const files = results?.files as EntityResult<any>;
  const folders = results?.folders as EntityResult<any>;
  const invitations = results?.invitations as EntityResult<any>;
  const companies = results?.companies as EntityResult<any>;

  const totalResults = useMemo(() => {
    const parts = [users, roles, files, folders, invitations, companies].filter(
      Boolean,
    ) as EntityResult<any>[];
    if (parts.length === 0) return 0;
    return parts.reduce((acc, r) => acc + (r?.total ?? r?.items?.length ?? 0), 0);
  }, [users, roles, files, folders, invitations, companies]);

  return (
    <div className="max-w-4xl">
      <h1 className="mb-6 text-3xl font-bold text-primary">Search Results</h1>

      {!query && (
        <p className="mt-10 text-xl text-center text-primary text-muted-foreground">
          Type something to search.
        </p>
      )}

      {query && totalResults === 0 && (
        <p className="mt-10 text-xl text-center text-primary text-muted-foreground">
          No matching results found.
        </p>
      )}

      <Section
        title="Folders"
        icon={<Folder className="w-5 h-5 text-blue-600" />}
        data={folders}
        loadingSection={pendingFolders}
        renderItem={(folder: any) => (
          <div key={folder.id} className="p-3 rounded-md shadow-sm bg-muted">
            <div className="font-medium text-primary">{folder.folderName}</div>
            {folder.description && (
              <p className="text-sm text-muted-foreground">{folder.description}</p>
            )}
          </div>
        )}
        onShowMore={() => {
          setPendingFolders(true);
          setTakeFolders((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingFolders(true);
          setTakeFolders(DEFAULT_TAKE);
        }}
        canShowLess={takeFolders > DEFAULT_TAKE}
      />

      <Section
        title="Files"
        icon={<FileText className="w-5 h-5 text-red-600" />}
        data={files}
        loadingSection={pendingFiles}
        renderItem={(file: any) => (
          <div
            key={file.id}
            className="p-3 transition rounded-md shadow-sm cursor-pointer bg-muted hover:bg-gray-100"
            onClick={() => window.open(`/files/${file.id}`, '_blank')}
          >
            <span className="font-medium text-primary">{file.editedName || file.fileName}</span>
          </div>
        )}
        onShowMore={() => {
          setPendingFiles(true);
          setTakeFiles((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingFiles(true);
          setTakeFiles(DEFAULT_TAKE);
        }}
        canShowLess={takeFiles > DEFAULT_TAKE}
      />

      <Section
        title="Users"
        icon={<User className="w-5 h-5 text-primary" />}
        data={users}
        loadingSection={pendingUsers}
        renderItem={(user: any) => (
          <div key={user.id} className="p-3 rounded-md shadow-sm bg-muted">
            <div className="font-medium text-primary">
              {user.firstName} {user.lastName}
            </div>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        )}
        onShowMore={() => {
          setPendingUsers(true);
          setTakeUsers((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingUsers(true);
          setTakeUsers(DEFAULT_TAKE);
        }}
        canShowLess={takeUsers > DEFAULT_TAKE}
      />

      <Section
        title="Roles"
        icon={<ShieldCheck className="w-5 h-5 text-green-600" />}
        data={roles}
        loadingSection={pendingRoles}
        renderItem={(role: any) => (
          <div key={role.id} className="p-3 rounded-md shadow-sm bg-muted">
            <div className="font-medium text-primary">{role.name}</div>
            {role.description && (
              <p className="text-sm text-muted-foreground">{role.description}</p>
            )}
          </div>
        )}
        onShowMore={() => {
          setPendingRoles(true);
          setTakeRoles((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingRoles(true);
          setTakeRoles(DEFAULT_TAKE);
        }}
        canShowLess={takeRoles > DEFAULT_TAKE}
      />

      <Section
        title="Invitations"
        icon={<Mail className="w-5 h-5 text-orange-500" />}
        data={invitations}
        loadingSection={pendingInvitations}
        renderItem={(inv: any) => (
          <div key={inv.id} className="p-3 rounded-md shadow-sm bg-muted">
            <div className="font-medium text-primary">{inv.email}</div>
            <p className="text-sm text-muted-foreground">
              Status: {inv.accepted ? 'Accepted' : inv.revoked ? 'Revoked' : 'Pending'}
            </p>
          </div>
        )}
        onShowMore={() => {
          setPendingInvitations(true);
          setTakeInvitations((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingInvitations(true);
          setTakeInvitations(DEFAULT_TAKE);
        }}
        canShowLess={takeInvitations > DEFAULT_TAKE}
      />

      <Section
        title="Companies"
        icon={<Building2 className="w-5 h-5 text-indigo-600" />}
        data={companies}
        loadingSection={pendingCompanies}
        renderItem={(company: any) => (
          <div key={company.id} className="p-3 rounded-md shadow-sm bg-muted">
            <div className="font-medium text-primary">{company.name}</div>
            <p className="text-sm text-muted-foreground">
              Country: {company.country?.name || 'N/A'}
            </p>
          </div>
        )}
        onShowMore={() => {
          setPendingCompanies(true);
          setTakeCompanies((v) => v + STEP);
        }}
        onShowLess={() => {
          setPendingCompanies(true);
          setTakeCompanies(DEFAULT_TAKE);
        }}
        canShowLess={takeCompanies > DEFAULT_TAKE}
      />
    </div>
  );
};
